// Job-scoped agreements (CR #199) — lists this job's work agreements / work
// orders and creates new ones pre-linked to the job + client via the existing
// agreement builder (/agreements/new?client_id=&job_id=).
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listAgreements, type Agreement } from '@/lib/api/agreements';
import { agreementPath } from '@/lib/utils/navPaths';

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  draft:     { label: 'Draft',     color: 'text-gray-600 bg-gray-100 border-gray-200' },
  sent:      { label: 'Sent',      color: 'text-blue-700 bg-blue-50 border-blue-200' },
  signed:    { label: 'Signed',    color: 'text-green-700 bg-green-50 border-green-200' },
  cancelled: { label: 'Cancelled', color: 'text-red-700 bg-red-50 border-red-200' },
};

// DATE columns arrive as "YYYY-MM-DD" (or full ISO) strings — parse at local noon.
const fmt = (d: string | null) =>
  d ? new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const fmtMoney = (v: number | string | null) =>
  v == null ? '—' : `$${Number(v).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const AgreementsTab = ({ jobId, clientId }: { jobId: string; clientId?: number }) => {
  const navigate = useNavigate();
  const [agreements, setAgreements] = useState<Agreement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    listAgreements({ job_id: jobId })
      .then(data => { if (alive) { setAgreements(data); setError(''); } })
      .catch(() => { if (alive) setError('Failed to load agreements'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [jobId]);

  const newAgreementHref = clientId
    ? `/agreements/new?client_id=${clientId}&job_id=${jobId}`
    : `/agreements/new`;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">
          {loading ? 'Loading…' : `${agreements.length} agreement${agreements.length === 1 ? '' : 's'} linked to this job`}
        </p>
        <button
          onClick={() => navigate(newAgreementHref)}
          className="flex items-center gap-1.5 h-9 px-3 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
        >
          <span className="material-symbols-outlined text-base">add</span>
          New Agreement
        </button>
      </div>

      {error ? (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          <span className="material-symbols-outlined text-base">error</span>
          {error}
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary/20 border-t-primary" />
        </div>
      ) : agreements.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-40 gap-2 text-center border border-dashed border-gray-200 rounded-xl">
          <span className="material-symbols-outlined text-gray-300 text-4xl">handshake</span>
          <p className="text-gray-500 text-sm font-medium">No agreements for this job yet</p>
          <p className="text-gray-400 text-xs">Create one — it will be pre-linked to this job and client.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Number</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Title</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden md:table-cell">Date</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Value</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {agreements.map(a => {
                  const sc = STATUS_CONFIG[a.status] || STATUS_CONFIG.draft;
                  return (
                    <tr
                      key={a.id}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(agreementPath(a.client_id, a.id))}
                    >
                      <td className="px-4 py-3 font-mono text-xs text-primary font-semibold whitespace-nowrap">{a.agreement_number}</td>
                      <td className="px-4 py-3 font-medium text-gray-900 max-w-xs">
                        <div className="flex items-center gap-2">
                          <p className="truncate">{a.title}</p>
                          {a.agreement_type === 'subcontractor_work_order' && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-600 border border-purple-200 font-semibold flex-shrink-0">WO</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500 hidden md:table-cell whitespace-nowrap">{fmt(a.agreement_date)}</td>
                      <td className="px-4 py-3 text-gray-700 font-medium whitespace-nowrap">{fmtMoney(a.contract_value)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${sc.color}`}>{sc.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
